/**
 * positionFrame — translate user-frame partPosition values into the
 * chain's intrinsic frame.
 *
 * Every `partPositionMm`-style value the host writes (mover position,
 * station start/end/stops, area start/end) is interpreted in the user's
 * frame and run through `userToChainPositionMm` before being fed to
 * `moverWorldAt` / `sampleChainPoint` / `sampleChainRange`. The opposite
 * direction (`chainToUserPositionMm`) lets the imperative
 * `getMoverPosition` API and the screenshot metadata report values back
 * in the host's frame.
 *
 * The transform is its own inverse when applied with the same frame, so
 * round-tripping through `chainToUser(userToChain(x))` always returns x.
 */

import type { PositionFrame, StationConfig } from '../types.js';

/** `chainPos = originMm + (direction === 'negative' ? -userPos : userPos)` */
export function userToChainPositionMm(
  userPos: number,
  frame?: PositionFrame,
): number {
  if (!frame) return userPos;
  const sign = frame.direction === 'negative' ? -1 : 1;
  return (frame.originMm ?? 0) + sign * userPos;
}

/** Inverse of `userToChainPositionMm`. */
export function chainToUserPositionMm(
  chainPos: number,
  frame?: PositionFrame,
): number {
  if (!frame) return chainPos;
  const sign = frame.direction === 'negative' ? -1 : 1;
  return sign * (chainPos - (frame.originMm ?? 0));
}

/**
 * Resolve a `StationConfig.stopPositions[i]` to an absolute (chain-frame)
 * partPosition value.
 *
 * Order of operations:
 *   1. If `stopPositionsRelativeTo === 'station'`, the value is treated
 *      as an offset from the station's `startPositionOnPart` first.
 *   2. The resulting value is then passed through `userToChainPositionMm`
 *      using the part's XPU `positionFrame` (if any).
 *
 * The user can therefore say "second stop is 100 mm into station 'Pickup',
 * regardless of where Pickup starts" while still benefiting from the
 * track's overall direction / origin convention.
 */
export function resolveStopPositionMm(
  stopValue: number,
  station: StationConfig,
  frame?: PositionFrame,
): number {
  const userMm =
    station.stopPositionsRelativeTo === 'station'
      ? station.startPositionOnPart + stopValue
      : stopValue;
  return userToChainPositionMm(userMm, frame);
}

/**
 * Chain-frame `s`-values that mark the user-frame START and END of a part
 * with the given `trackLengthMm`.
 *
 * For `direction: 'positive'` (default) this is the obvious
 * `[0, trackLengthMm]`. For `direction: 'negative'` the user's "forward"
 * runs from chain-frame `trackLengthMm` down to `0`, so the user-frame
 * start lands on `trackLengthMm` and the user-frame end on `0`.
 *
 * Used by multi-part Station / Area renderers so intermediate parts cover
 * their entire chain and the first/last segments terminate on the correct
 * end of the chain regardless of direction. Returning both endpoints in
 * one shot keeps the call sites tiny + obviously symmetric.
 */
export function chainEndpointsForPart(
  trackLengthMm: number,
  frame?: PositionFrame,
): { userStartChainMm: number; userEndChainMm: number } {
  const negative = frame?.direction === 'negative';
  return {
    userStartChainMm: negative ? trackLengthMm : 0,
    userEndChainMm: negative ? 0 : trackLengthMm,
  };
}
